/**
 * The nearest scrollable ancestor of `element`, falling back to `<html>`.
 */
function getScrollContainer(element: HTMLElement): Element {
  // Walk up the DOM to find the nearest scrollable ancestor.
  // Stop at <html> so we always have a valid fallback.
  let scrollContainer: Element | null = element.parentElement;

  while (
    scrollContainer &&
    scrollContainer !== globalThis.document.documentElement
  ) {
    const { overflowY } = globalThis.getComputedStyle(scrollContainer);

    if (overflowY === 'auto' || overflowY === 'scroll') {
      break;
    }

    scrollContainer = scrollContainer.parentElement;
  }

  return scrollContainer ?? globalThis.document.documentElement;
}

/**
 * Smoothly scrolls the nearest scrollable ancestor of `element` so that
 * `element` is brought into view, offset from the top by `offsetPx` pixels
 * (useful for clearing sticky headers or action bars).
 *
 * Unlike `element.scrollIntoView()`, this targets the actual scrollable
 * container rather than the window, which is necessary when the page content
 * lives inside an overflow-scroll element.
 *
 * @param element - The element to scroll into view.
 * @param offsetPx - Top offset in pixels to leave above the element.
 */
export function smoothScrollTo(element: HTMLElement, offsetPx = 48): void {
  const container = getScrollContainer(element);
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();

  // elementRect.top and containerRect.top are both relative to the viewport,
  // so their difference gives the element's position within the container.
  // Adding the current scrollTop converts that into an absolute scroll target.
  container.scrollTo({
    behavior: 'smooth',
    top: container.scrollTop + (elementRect.top - containerRect.top) - offsetPx,
  });
}

/**
 * Smoothly scrolls the nearest scrollable ancestor of `element` all the way up.
 *
 * @param element - Any element inside the container to scroll.
 */
export function smoothScrollToTop(element: HTMLElement): void {
  getScrollContainer(element).scrollTo({ behavior: 'smooth', top: 0 });
}
